import { css } from '@emotion/react';
import { ThemeProvider } from '@emotion/react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes } from 'react-router-dom';
import defaultTheme from 'shared/defaultTheme';
import { OverviewContainer, Footer } from 'shared/containers';
import { ToolLoader, HypotheekbondLogo, InternalServerError } from 'shared/components';
import { Disclaimer } from 'shared/wizard';
import { UPDATE } from 'shared/wizard/actions';
import { getSettings } from '../helpers';
import { lazyWithRetry, useTrackPageview } from "shared/utility";

const interestSheetPromise = import(
  /* webpackChunkName: "renteoverzicht-interest-sheet" */
  './InterestSheet'
);
const overviewPromise = import(
  /* webpackChunkName: "renteoverzicht-overview" */
  './Overview'
);
const topFivePromise = import(
  /* webpackChunkName: "renteoverzicht-top-five" */
  './TopFive'
);

const InterestSheet = lazyWithRetry(() => interestSheetPromise);
const Overview = lazyWithRetry(() => overviewPromise);
const TopFive = lazyWithRetry(() => topFivePromise);

const App = () => {
  const dispatch = useDispatch();
  const { app, framed, settings, broken, input } = useSelector(state => state);
  const [theme, setTheme] = useState(defaultTheme);
  const base = `/${app}${framed ? '/framed' : ''}`;

  useTrackPageview();

  useEffect(() => {
    getSettings(res => {
      const settings = res.data;
      const {
        firstProduct,
        secondProduct,
        thirdProduct,
        fourthProduct,
        fifthProduct,
        initialSortedFrt,
        isFlexFront,
      } = settings;
      const productMonths = [
        firstProduct.years,
        secondProduct.years,
        thirdProduct.years,
        fourthProduct.years,
        fifthProduct.years,
      ].map(years => years * 12);
      const frt = productMonths.map((months, index) => (months ? months : input.frt[index]));
      const sorted_frt = initialSortedFrt.years * 12;
      const prefer_collaboration = Boolean(isFlexFront);
      dispatch({ type: UPDATE, payload: { settings, input: { ...input, frt, sorted_frt, prefer_collaboration } } });
      setTheme({ ...theme, ...res.data });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!settings) return <ToolLoader />;

  const BrokenLayout = () => {
    const { label_id, product_id } = input;
    return (
      <div
        css={css`
          display: flex;
          justify-content: center;
          margin: 32px 0;
        `}
      >
        {label_id && !product_id ? (
          <InternalServerError
            header="Dit label is niet gevonden"
            buttonText="Terug naar het overzicht"
            onClickHandler={() => (window.location.href = '/renteoverzicht/all')}
          />
        ) : (
          <InternalServerError />
        )}
      </div>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      {broken ? (
        <BrokenLayout />
      ) : (
        <OverviewContainer>
          <Routes>
            <Route path={`${base}/all`} element={<Overview />} />
            <Route path={`${base}/interest-sheet/:labelId`} element={<InterestSheet />} />
            <Route path={`${base}`} element={<TopFive />} />
          </Routes>
          <Footer>
            {settings.enableDisclaimer && <Disclaimer settings={settings} />}
            {settings.enableHypotheekbondLogo && <HypotheekbondLogo />}
          </Footer>
        </OverviewContainer>
      )}
    </ThemeProvider>
  );
};

export default App;
